/**
 * PageNode — custom xyflow node type for the sitemap canvas (web render).
 *
 * Each xyflow node represents one page in the site. The node body
 * contains a frontninja2 Grid, allowing widget layout editing
 * directly on the canvas (T3.9).
 *
 * All logic (y-grid bindings, section seeding/reconciliation, zoom-preview
 * gating, rename, drop/nest-target derivations) lives in the shared
 * `usePageNode` hook so the native render (`PageNode.native.tsx`) reuses it
 * verbatim; this file is the web JSX (react-strict-dom html.*, data-page-grid
 * drop zone, double-click rename) only.
 */
import { useMemo, useEffect } from "react";
import { css, html } from "react-strict-dom";
import { colors } from "@duro-app/tokens/tokens/colors.css";
import {
  microSpacing,
  radii,
  spacing,
} from "@duro-app/tokens/tokens/spacing.css";
import { borders } from "@duro-app/tokens/tokens/borders.css";
import { typography, typeScale } from "@duro-app/tokens/tokens/typography.css";
import { shadows } from "@duro-app/tokens/tokens/shadows.css";
import { Handle, Position, type NodeProps } from "./xyflow.js";
import { Grid, InteractionLayer, useGridStore } from "@fredericrous/grid";
import { PageEditProvider } from "./PageEditContext.js";
import { PageLodProvider } from "./PageLodContext.js";
import { useCanvasZoomValue } from "./canvasZoom.js";
import { RegionSkeleton } from "./PageSkeleton.js";
import { EditorButton } from "./ui/EditorButton.js";
import { useThemeVars } from "./ThemeVarsContext.js";
import { useBoundariesRevealed } from "./resizeMode.js";
import { setPageSelected } from "./pageSelection.js";
import { useElementBounds } from "./useElementBounds.js";
import { usePageNode, PAGE_HEIGHT, type PageNodeData } from "./usePageNode.js";
import { MobileHomeIndicator } from "./DeviceChrome.js";
import { PageTitleBar } from "./PageTitleBar.js";
import { PageNameLabel } from "./PageNameLabel.js";
import { makeRenderWidget } from "./widgets/renderWidget.js";
import { pageFrameOpen } from "./pageFrameOpen.js";
import { HEADER_GRID_ID, FOOTER_GRID_ID } from "./sharedRegions.js";
import { sizes } from "@duro-app/tokens/tokens/sizes.css";

export type { PageNodeData };

// Longhand only, per react-strict-dom; anything that varies at runtime goes
// through a style function so StyleX compiles it to a CSS custom property.
const s = css.create({
  // "🔒 shared" chip pinned to the band's top-right corner.
  bandChip: {
    position: "absolute",
    top: 0,
    right: 0,
    zIndex: 4,
    fontSize: "0.6rem",
    fontWeight: typography.fontWeightSemibold,
    color: colors.accent,
    backgroundColor: colors.infoBg,
    paddingTop: microSpacing.px1,
    paddingBottom: microSpacing.px1,
    paddingLeft: microSpacing.px6,
    paddingRight: microSpacing.px6,
    borderBottomLeftRadius: radii.xs,
    pointerEvents: "none",
  },
  // Wrapper: relative + NOT clipping, so the name label can sit ABOVE the
  // frame (Figma-style). The framed page box is the clipping child below it.
  wrap: {
    position: "relative",
  },
  wrapWidth: (width: number) => ({ width }),
  frame: {
    position: "relative",
    width: "100%",
    backgroundColor: colors.fixedLight,
    borderWidth: borders.hairline,
    borderStyle: "solid",
    borderRadius: radii.sm,
    display: "flex",
    flexDirection: "column",
  },
  // PAGE_HEIGHT is an imported constant, which StyleX cannot read at build
  // time (only `.stylex.*` var files resolve), so it rides in as a param.
  frameMinHeight: (minHeight: number) => ({ minHeight }),
  frameNest: (nest: boolean) => ({
    borderColor: nest ? "rgb(37, 99, 235)" : "rgb(229, 231, 235)",
    boxShadow: nest
      ? "0 0 0 3px rgba(37, 99, 235, 0.45)"
      : "0 1px 4px rgba(0, 0, 0, 0.06)",
  }),
  frameOpen: (open: boolean) => ({
    overflow: open ? "visible" : "hidden",
  }),
  nestBar: {
    position: "absolute",
    left: 0,
    right: 0,
    height: sizes.barH,
    backgroundColor: colors.accent,
    zIndex: 5,
    pointerEvents: "none",
  },
  nestBarEdge: (mode: "parent" | "child") => ({
    top: mode === "parent" ? 0 : "auto",
    bottom: mode === "child" ? 0 : "auto",
  }),
  nestBadgeBox: {
    position: "absolute",
    left: "50%",
    zIndex: 6,
    pointerEvents: "none",
  },
  // Counter-scale against the canvas zoom so the label stays readable
  // at any zoom (anchored to the edge it sits on).
  nestBadgePos: (mode: "parent" | "child", scale: number) => ({
    top: mode === "parent" ? 10 : "auto",
    bottom: mode === "child" ? 10 : "auto",
    transform: `translateX(-50%) scale(${scale})`,
    transformOrigin: mode === "parent" ? "top center" : "bottom center",
  }),
  nestBadge: {
    display: "inline-block",
    whiteSpace: "nowrap",
    backgroundColor: colors.accent,
    color: colors.accentContrast,
    fontSize: typeScale.fontSize2,
    fontWeight: typography.fontWeightBold,
    lineHeight: 1,
    paddingTop: microSpacing.px6,
    paddingBottom: microSpacing.px6,
    paddingLeft: spacing.sm,
    paddingRight: spacing.sm,
    borderRadius: radii.px6,
    boxShadow: shadows.sm,
  },
  urlButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: sizes.chip,
    height: sizes.chip,
    paddingTop: 0,
    paddingRight: 0,
    paddingBottom: 0,
    paddingLeft: 0,
    borderWidth: 0,
    borderStyle: "none",
    borderRadius: radii.px6,
    backgroundColor: "transparent",
    color: colors.textMuted,
    cursor: "pointer",
    flexShrink: 0,
  },
});

/**
 * A shared region band (site header/footer) rendered on every page. It renders
 * the SAME grid on every page (bound once by useSharedRegions), so editing here
 * updates all pages. A dashed outline + chip mark it as global.
 */
function SharedRegionBand({
  region,
  gridId,
  widgetPreview,
  themeVars,
}: {
  region: "Header" | "Footer";
  gridId: string;
  widgetPreview: boolean;
  themeVars: React.CSSProperties;
}) {
  // Render only once the shared grid is bound (useSharedRegions, on the canvas).
  const canvasZoom = useCanvasZoomValue();
  // "Boundaries" on the selection bar: reveal this band's splitter strips so
  // the shared-edge verb can be found without a hover (grid 2.2's
  // `splitterReveal` paints them and widens them on coarse pointers).
  const boundaries = useBoundariesRevealed(gridId);
  const exists = useGridStore((st) => !!st.grids[gridId]);
  if (!exists) return null;
  return (
    // eslint-disable-next-line duro/no-raw-html-element -- spreads themeVars, runtime `--wb-*` custom properties RSD's StyleX-only `style` prop cannot carry.
    <div
      style={{
        ...themeVars,
        position: "relative",
        flexShrink: 0,
        background: "var(--wb-color-background)",
        color: "var(--wb-color-foreground)",
        fontFamily: "var(--wb-font-body)",
        outlineWidth: borders.hairline,
        outlineStyle: "dashed",
        outlineColor: colors.highlight,
        outlineOffset: `calc(-1 * ${borders.focusOffsetSm})`,
      }}
    >
      <html.div style={s.bandChip}>
        🔒 {region} · shared — edits apply to all pages
      </html.div>
      {widgetPreview ? (
        <Grid
          gridId={gridId}
          preview={true}
          renderItem={makeRenderWidget(gridId, true)}
        />
      ) : (
        // InteractionLayer makes the items draggable/resizable on the grid
        // (move the logo + menu horizontally), same as a section's nested grid.
        <InteractionLayer>
          <Grid
            gridId={gridId}
            preview={false}
            renderItem={makeRenderWidget(gridId, false)}
            multiSelect
            lineResize
            gapResize
            visualScale={canvasZoom}
            splitterReveal={boundaries ? "always" : "hover"}
          />
        </InteractionLayer>
      )}
    </div>
  );
}

export function PageNode({ id, data, selected, dragging }: NodeProps) {
  const {
    label,
    slugOverride,
    readOnly,
    lod,
    breakpoint,
    authoringWidth,
    pagePreview,
    widgetPreview,
    isNestTarget,
    nestMode,
    nestBadgeScale,
    editingLabel,
    setEditingLabel,
    draftLabel,
    setDraftLabel,
    startRename,
    commitRename,
    renderWidget,
    menuButton,
    openNodeMenu,
    openSlugEditor,
    editingItemId,
    setEditingItemId,
  } = usePageNode({ id, data });
  const canvasZoom = useCanvasZoomValue();
  // Publish this page's xyflow selection for the selection bar's PAGE state
  // (section spacing). Cleared on deselect and on unmount.
  useEffect(() => {
    setPageSelected({ pageId: id, label }, !!selected);
    return () => setPageSelected({ pageId: id, label }, false);
  }, [id, label, selected]);
  const themeVars = useThemeVars();
  // The slug popover anchors on the pencil's rect. RSD's click event carries
  // no `currentTarget`, so measure the element itself — same seam the ⋯ menu
  // already uses (useElementBounds).
  const urlButton = useElementBounds<HTMLButtonElement>();
  // Edit affordances belong to the resting SELECTED state; a drag is a spatial
  // task, so suppress them mid-move and restore on drop (xyflow `dragging`).
  const canEditUrl = !readOnly && !!selected && !dragging && id !== "home";
  // Select→drill-in signal for this page's editable grids (body sections +
  // shared header/footer): a text widget announces edit mode here so the caret
  // is gated behind an explicit drill-in. setEditingItemId is stable (useState);
  // memoise so the subtree only re-renders when the edited item actually flips.
  const pageEdit = useMemo(
    () => ({ editingItemId, setEditingItemId }),
    [editingItemId, setEditingItemId],
  );

  // Open the frame's clip while a drag involves this page's grids — the
  // dragged widget follows the pointer as a real element inside its source
  // grid and was getting cropped at the page edges (see pageFrameOpen).
  // Boolean selector → re-renders only on the transition.
  const frameOpen = useGridStore((st) =>
    pageFrameOpen(st.interaction, (gid) => st.grids[gid]?.parentGridId, id),
  );

  return (
    <PageEditProvider value={pageEdit}>
      <html.div style={[s.wrap, s.wrapWidth(authoringWidth)]}>
        {/* Page NAME — the canvas/builder identity, outside the frame, top-left.
          Distinct from the site ADDRESS shown inside the titlebar. */}
        <PageNameLabel
          label={label}
          readOnly={readOnly}
          selected={!!selected}
          dragging={!!dragging}
          scale={nestBadgeScale}
          editingLabel={editingLabel}
          draftLabel={draftLabel}
          setDraftLabel={setDraftLabel}
          startRename={startRename}
          commitRename={commitRename}
          cancelRename={() => setEditingLabel(false)}
        />
        <html.div
          style={[
            s.frame,
            s.frameMinHeight(PAGE_HEIGHT),
            s.frameNest(isNestTarget),
            s.frameOpen(frameOpen),
          ]}
        >
          {/* Drag-to-parent affordance. The accent + badge sit at the TOP when the
          dropped page would become THIS page's parent, at the BOTTOM when it
          nests as a child — matching where the pointer is over the node. */}
          {isNestTarget && (
            <>
              <html.div style={[s.nestBar, s.nestBarEdge(nestMode)]} />
              <html.div
                style={[
                  s.nestBadgeBox,
                  s.nestBadgePos(nestMode, nestBadgeScale),
                ]}
              >
                <html.span style={s.nestBadge}>
                  {nestMode === "parent"
                    ? "⤴ Drop as parent"
                    : "⤵ Drop as child"}
                </html.span>
              </html.div>
            </>
          )}
          {/* The fused titlebar: the device frame IS the page header — the site
          ADDRESS (display-only) + ⋯ menu, flavoured per device. It's an xyflow
          drag handle (`.wb-page-drag`). The editable NAME lives in the label
          above the frame; home shows the bare domain (its real path is `/`). */}
          <PageTitleBar
            breakpoint={breakpoint}
            label={label}
            slugOverride={slugOverride}
            readOnly={readOnly}
            isHome={id === "home"}
            urlEditSlot={
              canEditUrl ? (
                <html.button
                  ref={urlButton.ref}
                  type="button"
                  aria-label="Change URL"
                  onPointerDown={(e: { stopPropagation: () => void }) =>
                    e.stopPropagation()
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    const r = urlButton.measure();
                    if (r)
                      openSlugEditor(id, r.left, r.bottom, label, slugOverride);
                  }}
                  style={s.urlButton}
                >
                  {/* holds-until: Duro Icon has a link glyph (https://github.com/fredericrous/duro-design-system/issues/107) */}
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <path d="M12 20h9" />
                    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                  </svg>
                </html.button>
              ) : undefined
            }
            menuSlot={
              !readOnly ? (
                <EditorButton
                  ref={menuButton.ref}
                  aria-label={`Actions for ${label}`}
                  title="Page actions"
                  isolatePress
                  onClick={() => {
                    const r = menuButton.measure();
                    if (r) openNodeMenu(id, r.left, r.bottom);
                  }}
                >
                  ⋯
                </EditorButton>
              ) : null
            }
          />

          {/* Site HEADER — shared across all pages (edit here → updates
          everywhere). At far zoom it's a cheap wireframe band like the sections,
          so the skeleton page still reads header · body · footer. */}
          {lod === "wireframe" ? (
            <RegionSkeleton
              gridId={HEADER_GRID_ID}
              width={authoringWidth}
              edge="top"
            />
          ) : (
            <SharedRegionBand
              region="Header"
              gridId={HEADER_GRID_ID}
              widgetPreview={widgetPreview}
              themeVars={themeVars as React.CSSProperties}
            />
          )}

          {/* Grid body — the page grid is the stack of section blocks. It
          stays interactive (preview=false) unless read-only, so sections
          reorder at any zoom; each section's nested grid gates editing on
          zoom via widgetPreview (passed through renderWidget). */}
          {/* data-page-grid marks the WHOLE page body as the section drop zone
          (incl. over existing sections): AddDragContext climbs to it via
          closest("[data-page-grid]"), so a section drag no longer has to hit
          the 1px gap between blocks. */}
          {/* eslint-disable-next-line duro/no-raw-html-element -- spreads themeVars, runtime `--wb-*` custom properties RSD's StyleX-only `style` prop cannot carry. */}
          <div
            data-page-grid={id}
            style={{
              // Resolved theme tokens cascade to the grid + widgets (WYSIWYG with
              // the published render); the page body shows the themed surface.
              ...(themeVars as React.CSSProperties),
              flex: 1,
              minHeight: 0,
              position: "relative",
              background: "var(--wb-color-background)",
              color: "var(--wb-color-foreground)",
              fontFamily: "var(--wb-font-body)",
            }}
          >
            {/* The page grid stays MOUNTED at every zoom band (incl. far-zoom
            'wireframe') so sections reorder + drag-add preview at any zoom; the
            LOD flows to each SectionWidget, which draws its CONTENT as a cheap
            SVG wireframe at 'wireframe' (the far-zoom perf win now lives per-
            section, not by swapping the whole grid for a static skeleton). */}
            <PageLodProvider value={lod}>
              {!pagePreview ? (
                <InteractionLayer>
                  {/* gapResize: drag the gutter between two sections to tune the
                page's vertical rhythm (Alt not needed — lineResize is off
                because section heights are auto-grow-owned and a manual
                boundary drag would be re-fit away on the next commit). */}
                  <Grid
                    gridId={id}
                    preview={false}
                    renderItem={renderWidget}
                    gapResize
                    multiSelect
                    visualScale={canvasZoom}
                  />
                </InteractionLayer>
              ) : (
                <Grid gridId={id} preview={true} renderItem={renderWidget} />
              )}
            </PageLodProvider>
          </div>

          {/* Site FOOTER — shared across all pages (wireframe band at far zoom). */}
          {lod === "wireframe" ? (
            <RegionSkeleton
              gridId={FOOTER_GRID_ID}
              width={authoringWidth}
              edge="bottom"
            />
          ) : (
            <SharedRegionBand
              region="Footer"
              gridId={FOOTER_GRID_ID}
              widgetPreview={widgetPreview}
              themeVars={themeVars as React.CSSProperties}
            />
          )}

          {/* iOS home indicator caps the tablet / mobile frame. */}
          {lod !== "wireframe" && breakpoint !== "desktop" && (
            <MobileHomeIndicator />
          )}

          {/* xyflow connection handles (hidden in read-only preview) */}
          {!readOnly && (
            <>
              <Handle type="target" position={Position.Top} />
              <Handle type="source" position={Position.Bottom} />
            </>
          )}
        </html.div>
      </html.div>
    </PageEditProvider>
  );
}
